import { BrowserProvider, getAddress, type Eip1193Provider } from 'ethers'
import { CHAIN_ID, MAIN_SAFE } from './constants.js'
import { concatSafeSignatures, encodeExecTransaction } from './safe.js'
import { SafewardClient } from '../safeward-client.js'
import type { Hex, PendingTx } from '../types.js'

/**
 * End-to-end "execute a 2/2-signed Module Safe tx on-chain" pipeline,
 * extracted out of TxCard so the orchestration is testable in isolation.
 *
 * Stages (all surfaced via onProgress):
 *   1. simulate    — re-run /v1/decide; non-blocking. State drift between
 *                    propose-time and execute-time can cause stale txs to
 *                    revert (slippage, deadlines). The result is informational
 *                    only; the wallet popup is the actual confirmation gate.
 *   2. send        — concat sigs, build calldata, ask the wallet to broadcast.
 *   3. wait        — wait for receipt and classify the outcome.
 *
 * Inner reverts: the EVM tx can return status=1 while the inner module call
 * reverted. Safe captures inner reverts as ExecutionFromModuleFailure /
 * ExecutionFailure events instead of bubbling them, so we scan the logs.
 */

export const SAFE_EXECUTION_FAILURE_TOPIC =
  '0x23428b18acfb3ea64b08dc0c1d296ea9c09702c09083ca5272e64d115b687d23'
export const SAFE_EXECUTION_FROM_MODULE_FAILURE_TOPIC =
  '0xacd2c8702804128fdb0db2bb49f6d127dd0181c13fd45dbfe16de0930e2bd375'

export interface ExecuteArgs {
  tx: PendingTx
  walletProvider: Eip1193Provider
  signerAddress: string
  moduleSafe: string
  safewardUrl: string
  safewardAuthToken: string
  onProgress?: (stage: ExecStage) => void
}

export type ExecStage =
  | { kind: 'simulating' }
  | { kind: 'simulated'; approved: boolean; reason: string }
  | { kind: 'sim-error'; message: string }
  | { kind: 'awaiting-wallet' }
  | { kind: 'broadcast'; txHash: string }
  | { kind: 'waiting-receipt'; txHash: string }

export type ExecOutcome =
  | { kind: 'success'; txHash: string }
  | { kind: 'reverted'; txHash: string }
  | { kind: 'inner-revert'; txHash: string; failedAt: 'main' | 'module' }

export interface ReceiptLogLike {
  address: string
  topics: ReadonlyArray<string>
}

export interface ReceiptLike {
  status: number | null
  hash: string
  logs: ReadonlyArray<ReceiptLogLike>
}

/**
 * Pure: scan receipt logs for a Safe inner-revert event. Returns 'main' if
 * the Main Safe (the one that owns the assets) emitted ExecutionFromModule-
 * Failure, 'module' if the Module Safe (the proxy) emitted ExecutionFailure,
 * or null if neither.
 */
export function findInnerRevertLog(
  logs: ReadonlyArray<ReceiptLogLike>,
  moduleSafe: string,
): 'main' | 'module' | null {
  const mainLower = MAIN_SAFE.toLowerCase()
  const moduleLower = moduleSafe.toLowerCase()
  for (const log of logs) {
    const t0 = log.topics[0]
    if (
      t0 === SAFE_EXECUTION_FROM_MODULE_FAILURE_TOPIC &&
      log.address.toLowerCase() === mainLower
    ) {
      return 'main'
    }
    if (
      t0 === SAFE_EXECUTION_FAILURE_TOPIC &&
      log.address.toLowerCase() === moduleLower
    ) {
      return 'module'
    }
  }
  return null
}

/**
 * Pure: classify a receipt as success / EVM-reverted / inner-reverted. Splits
 * the branching logic out so it can be unit-tested without spinning up a
 * provider or wallet.
 */
export function classifyExecResult(
  receipt: ReceiptLike | null,
  moduleSafe: string,
): ExecOutcome {
  if (!receipt || receipt.status !== 1) {
    return { kind: 'reverted', txHash: receipt?.hash ?? '0x' }
  }
  const inner = findInnerRevertLog(receipt.logs, moduleSafe)
  if (inner) {
    return { kind: 'inner-revert', txHash: receipt.hash, failedAt: inner }
  }
  return { kind: 'success', txHash: receipt.hash }
}

export async function executeModuleTx(args: ExecuteArgs): Promise<ExecOutcome> {
  const {
    tx,
    walletProvider,
    signerAddress,
    moduleSafe,
    safewardUrl,
    safewardAuthToken,
    onProgress,
  } = args

  // Pre-flight simulation: informational only. We surface the result via
  // onProgress and continue regardless — the wallet popup is the real gate.
  onProgress?.({ kind: 'simulating' })
  try {
    const client = new SafewardClient({
      baseUrl: safewardUrl,
      authToken: safewardAuthToken,
    })
    const fresh = await client.decide(tx)
    onProgress?.({
      kind: 'simulated',
      approved: fresh.decision.approved,
      reason: fresh.decision.reason,
    })
  } catch (err) {
    onProgress?.({ kind: 'sim-error', message: (err as Error).message })
  }

  const signatures = concatSafeSignatures(tx.confirmations)
  const data = encodeExecTransaction(tx, signatures)
  onProgress?.({ kind: 'awaiting-wallet' })
  const browser = new BrowserProvider(walletProvider, CHAIN_ID)
  const signer = await browser.getSigner(getAddress(signerAddress))
  const txResp = await signer.sendTransaction({
    to: getAddress(moduleSafe),
    data: data as Hex,
  })
  onProgress?.({ kind: 'broadcast', txHash: txResp.hash })
  onProgress?.({ kind: 'waiting-receipt', txHash: txResp.hash })

  const receipt = await txResp.wait()
  return classifyExecResult(
    receipt
      ? {
          status: receipt.status,
          hash: receipt.hash,
          logs: receipt.logs.map((l) => ({ address: l.address, topics: [...l.topics] })),
        }
      : null,
    moduleSafe,
  )
}
